import moment from "moment-timezone";

/**
 * Format game date for display
 * Shows "Today", "Tomorrow", or full date based on proximity
 * @param {string} dateString - ISO date string
 * @param {boolean} showTimeZone - Whether to show timezone abbreviation
 * @returns {string} Formatted date string
 */
export const formatGameDate = (dateString, showTimeZone = true) => {
  const gameMoment = moment(dateString);
  const now = moment(); // This will use local timezone
  
  // Check if game is today or tomorrow using local timezone
  if (gameMoment.isSame(now, "day")) {
    const timeFormat = showTimeZone ? "h:mm A z" : "h:mm A";
    return `Today ${gameMoment.format(timeFormat)}`;
  } else if (gameMoment.isSame(now.clone().add(1, "day"), "day")) {
    const timeFormat = showTimeZone ? "h:mm A z" : "h:mm A";
    return `Tomorrow ${gameMoment.format(timeFormat)}`;
  } else {
    // Show full date and time in user's local timezone
    const format = showTimeZone ? "ddd, MMM D, h:mm A z" : "ddd, MMM D, h:mm A";
    return gameMoment.format(format);
  }
};

/**
 * Format date for simple display (date only)
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string
 */
export const formatSimpleDate = (dateString) => {
  return moment(dateString).format("MMM D, YYYY");
};

/**
 * Format date and time for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (dateString) => {
  return moment(dateString).format("MMM D, YYYY h:mm A");
};

/**
 * Convert UTC date string to local datetime input value
 * @param {string} utcString - UTC ISO date string
 * @returns {string} Local datetime string for input field
 */
export const utcToLocalDateTimeInput = (utcString) => {
  if (!utcString) return "";
  return moment.utc(utcString).local().format("YYYY-MM-DDTHH:mm");
};

/**
 * Convert local datetime input value to UTC ISO string
 * @param {string} dateTimeLocal - Local datetime string from input field
 * @returns {string} UTC ISO date string
 */
export const localDateTimeToUTC = (dateTimeLocal) => {
  return moment(dateTimeLocal).utc().toISOString();
};

/**
 * Check if a game is within a certain number of hours
 * @param {string} gameDate - Game date string
 * @param {number} hours - Number of hours to check
 * @returns {boolean} True if game is within the specified hours
 */
export const isGameWithinHours = (gameDate, hours) => {
  const gameMoment = moment(gameDate);
  const now = moment(); // Uses local timezone
  return (
    gameMoment.diff(now, "hours") <= hours && gameMoment.diff(now, "hours") > 0
  );
};

/**
 * Get relative time string (e.g., "2 hours ago", "in 3 days")
 * @param {string} dateString - ISO date string
 * @returns {string} Relative time string
 */
export const getRelativeTime = (dateString) => {
  return moment(dateString).fromNow();
};

/**
 * Check if a date is today
 * @param {string} dateString - ISO date string
 * @returns {boolean} True if date is today
 */
export const isToday = (dateString) => {
  return moment(dateString).isSame(moment(), "day"); // Uses local timezone
};

/**
 * Check if a date is tomorrow
 * @param {string} dateString - ISO date string
 * @returns {boolean} True if date is tomorrow
 */
export const isTomorrow = (dateString) => {
  return moment(dateString).isSame(moment().add(1, "day"), "day"); // Uses local timezone
};

/**
 * Get the start and end dates for a specific NFL week
 * NFL weeks run Thursday to Wednesday
 * @param {number} weekNumber - Week number (1-18)
 * @param {number} seasonYear - Season year
 * @returns {Object} Object with weekStart (Thursday) and weekEnd (Wednesday) as Date objects
 */
export const getWeekBoundaries = (weekNumber, seasonYear) => {
  // Find the first Thursday of September for the given year
  const septemberFirst = moment(`${seasonYear}-09-01`);
  const firstThursday = septemberFirst.clone().day(4); // Thursday is day 4
  if (septemberFirst.day() > 4) {
    // If September 1st is after Thursday, get the next Thursday
    firstThursday.add(7, 'days');
  }
  
  // Calculate the start of the requested week (Thursday 00:00:00)
  const weekStart = firstThursday.clone().add((weekNumber - 1) * 7, 'days').startOf('day');
  
  // Calculate the end of the week (Wednesday 23:59:59)
  const weekEnd = weekStart.clone().add(6, 'days').endOf('day');
  
  return {
    weekStart: weekStart.toDate(),
    weekEnd: weekEnd.toDate(),
  };
};
